import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { isV2Form } from '@components/form-builder';
import { addFieldIdToForm } from '@components/form/dynamic-fields';
import { submitErrorTranslations } from '@components/form/v2/submit-error-translations';
import { ROTATING_LOADING_MIN_DURATION } from '@components/rotating-loading-messages';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { createFieldNameToFieldIdMap, remapConditionProperties } from 'utils/prepare-form';

import {
  FormResponse,
  JourneyFormResponse,
  loadForm,
  loadJourneyForm,
  loadJourneyFormBySlug,
  saveForm,
  saveJourneyForm,
} from './request';

export type JourneyFormIdentifier =
  | { kind: 'uuid'; journeyUuid: string; formUuid: string }
  | { kind: 'slug'; companySlug: string; formSlug: string };

export const useForm = (uuid: string) => {
  return useQuery({
    queryKey: ['form', uuid],
    staleTime: Infinity,
    queryFn: async () => {
      const data = await loadForm(uuid);

      if (isV2Form(data.fieldsConfig)) {
        return data;
      }

      // Make sure all fields have unique IDs
      const formWithIds = addFieldIdToForm(data.fieldsConfig);

      // Next we need to re-map field condition `name`s to their corresponding unique IDs
      const nameIdMap = createFieldNameToFieldIdMap(formWithIds);
      const remapped = remapConditionProperties(formWithIds, nameIdMap);

      return {
        ...data,
        fieldsConfig: remapped,
      } as FormResponse;
    },
  });
};

export const useJourneyForm = (identifier: JourneyFormIdentifier) => {
  const queryKey =
    identifier.kind === 'uuid'
      ? ['journey-form', 'uuid', identifier.journeyUuid, identifier.formUuid]
      : ['journey-form', 'slug', identifier.companySlug, identifier.formSlug];
  return useQuery({
    queryKey,
    staleTime: Infinity,
    queryFn: () => {
      return identifier.kind === 'uuid'
        ? loadJourneyForm(identifier.journeyUuid, identifier.formUuid)
        : loadJourneyFormBySlug(identifier.companySlug, identifier.formSlug);
    },
  });
};

export const useSaveForm = (uuid: string) => {
  const queryClient = useQueryClient();
  const submitErrorMessage = useSubmitErrorMessage();

  return useMutation({
    mutationFn: (formData: FormData) => saveForm(uuid, formData),
    onSuccess: (data) => {
      if (data.data.isComplete === false) {
        queryClient.refetchQueries({ queryKey: ['form', uuid] });
      }
    },
    onError: (error: AxiosError<SubmitError>) => {
      const message = submitErrorMessage(error);
      if (!message) return;
      setTimeout(() => showError(message), ROTATING_LOADING_MIN_DURATION);
    },
  });
};

export const useSaveJourneyForm = ({
  journeyUuid,
  uuid,
}: Pick<JourneyFormResponse, 'journeyUuid' | 'uuid'>) => {
  const submitErrorMessage = useSubmitErrorMessage();
  return useMutation({
    mutationFn: (formData: FormData) => {
      return saveJourneyForm(journeyUuid, uuid, formData);
    },
    onError: (error: AxiosError<SubmitError>) => {
      const message = submitErrorMessage(error);
      if (!message) return;
      setTimeout(() => showError(message), ROTATING_LOADING_MIN_DURATION);
    },
  });
};

// The toast is a fixed 424px and does not break long words, and the file name is the
// part the recipient needs to read.
const MAX_FILE_NAME_LENGTH = 36;

const shortFileName = (fileName: string) =>
  fileName.length <= MAX_FILE_NAME_LENGTH
    ? fileName
    : `${fileName.slice(0, MAX_FILE_NAME_LENGTH - 12)}…${fileName.slice(-11)}`;

type SubmitError = {
  error?: string;
  violations?: Array<{
    fileName?: string;
    fieldLabel?: string;
    accept?: string;
    maxFileSizeMb?: number;
  }>;
};

// The API rejects a submission with a code and, for a file's type or size, says which files it
// refused; the recipient's own language lives here. Both submit endpoints share the
// codes, so they share the mapping.
const useSubmitErrorMessage = () => {
  const { t } = useScopedTranslate(translations);

  return (error: AxiosError<SubmitError>) => {
    const data = error.response?.data;
    if (error.response?.status === 429) {
      return t('tooManyRequests');
    }
    if (!data?.error) {
      return t('submitFailed');
    }
    if (data.error === 'form_closed') {
      return undefined;
    }

    if (data.error === 'invalid_file_type') {
      const violations = data.violations ?? [];
      const [violation] = violations;
      // One refused file is the common case, and naming its field and the formats it
      // does accept is the whole difference between "try again" and knowing what to do.
      if (violations.length === 1 && violation?.fileName && violation.accept) {
        return violation.fieldLabel
          ? t('invalidFileTypeForField', {
              file: shortFileName(violation.fileName),
              field: violation.fieldLabel,
              accept: violation.accept,
            })
          : t('invalidFileTypeNamed', { file: shortFileName(violation.fileName) });
      }
      const names = violations
        .map((each) => each.fileName)
        .filter((fileName): fileName is string => Boolean(fileName))
        .map(shortFileName);
      if (names.length === 0) return t('invalidFileType');
      return names.length === 1
        ? t('invalidFileTypeNamed', { file: names[0] })
        : t('invalidFileTypeNamedMany', { files: names.join(', ') });
    }

    if (data.error === 'file_too_large') {
      const violations = data.violations ?? [];
      const [violation] = violations;
      if (violations.length === 1 && violation?.fileName && violation.maxFileSizeMb) {
        return t('fileTooLargeNamed', {
          file: shortFileName(violation.fileName),
          limit: violation.maxFileSizeMb,
        });
      }
      return t('fileTooLarge');
    }

    const messages: Record<string, string> = {
      missing_required_fields: t('missingRequiredFields'),
      invalid_email: t('invalidEmail'),
      invalid_fields_config: t('invalidFieldsConfig'),
    };
    return messages[data.error] ?? t('submitFailed');
  };
};

const translations = {
  en: {
    ...submitErrorTranslations.en,
    missingRequiredFields: 'Please fill in your name and email before submitting.',
    invalidEmail: 'Please enter a valid email address.',
    invalidFileTypeNamed: '{file} is not an accepted file format.',
    invalidFileTypeNamedMany: 'These files are not an accepted format: {files}',
    invalidFileTypeForField: '{file} is not accepted for {field}. Accepted formats: {accept}',
    invalidFieldsConfig: 'This form has changed. Please reload the page and try again.',
    tooManyRequests: 'Too many requests just now. Please wait a moment and try again.',
    submitFailed: 'We could not submit your answers. Please try again in a moment.',
    fileTooLargeNamed: '{file} is over the {limit} MB limit.',
  },
  is: {
    ...submitErrorTranslations.is,
    missingRequiredFields: 'Vinsamlegast fylltu út nafn og netfang áður en þú sendir.',
    invalidEmail: 'Vinsamlegast sláðu inn gilt netfang.',
    invalidFileTypeNamed: '{file} er ekki á leyfilegu skráarsniði.',
    invalidFileTypeNamedMany: 'Þessar skrár eru ekki á leyfilegu skráarsniði: {files}',
    invalidFileTypeForField: '{file} er ekki leyfð fyrir {field}. Leyfileg snið: {accept}',
    invalidFieldsConfig:
      'Þetta form hefur breyst. Vinsamlegast endurhladdu síðuna og prófaðu aftur.',
    tooManyRequests: 'Of margar beiðnir í augnablikinu. Bíddu andartak og reyndu aftur.',
    submitFailed: 'Ekki tókst að senda svörin þín. Reyndu aftur eftir augnablik.',
    fileTooLargeNamed: '{file} er yfir {limit} MB hámarkinu.',
  },
  dk: {
    ...submitErrorTranslations.dk,
    missingRequiredFields: 'Udfyld venligst dit navn og din e-mail, før du sender.',
    invalidEmail: 'Indtast venligst en gyldig e-mailadresse.',
    invalidFileTypeNamed: '{file} er ikke et tilladt filformat.',
    invalidFileTypeNamedMany: 'Disse filer er ikke et tilladt format: {files}',
    invalidFileTypeForField: '{file} er ikke tilladt for {field}. Tilladte formater: {accept}',
    invalidFieldsConfig: 'Denne formular er ændret. Genindlæs venligst siden og prøv igen.',
    tooManyRequests: 'For mange forespørgsler lige nu. Vent et øjeblik, og prøv igen.',
    submitFailed: 'Vi kunne ikke sende dine svar. Prøv venligst igen om et øjeblik.',
    fileTooLargeNamed: '{file} overskrider grænsen på {limit} MB.',
  },
};
